import { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { keyBy } from 'lodash';
import createUseContext from 'constate';
import firebase from '../../preorder/services/firebase';
import { getCoupons as apiGetCoupons, setPriorityCoupon as apiSetPriorityCoupon } from '../api/coupon';
import { useAuthState } from '../../preorder/plugins/Auth';

const dataPath = `/${process.env.REACT_APP_MODE}/customerCouponFrontItems/data`;
const dataRef = firebase.database().ref(dataPath);

const useCouponsStore = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isInited, setIsInited] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [couponsDict, setCouponsDict] = useState({});
  const [priorityCouponId, setPriorityCouponId] = useState('');
  const [customerCouponFrontItems, setCustomerCouponFrontItems] = useState([]);
  const [voucherShopItems, setVoucherShopItems] = useState([]);
  const [voucherShopItemDict, setVoucherShopItemDict] = useState({});
  const { token } = useAuthState();

  useEffect(() => {
    dataRef.on('value', (snapshot) => {
      const data = snapshot.val();
      setCustomerCouponFrontItems(data || []);
    }, (e) => {
      console.log(e.message);
    });
    return () => { dataRef.off('value'); };
  }, [setCustomerCouponFrontItems]);

  const loadCoupons = useCallback(async () => {
    const result = await apiGetCoupons(token);
    const { status, data, message } = result || {};
    const {
      coupons: dataCoupons,
      priorityCouponId: dataPriorityCouponId,
      voucherShopItems: dataVoucherShopItems,
    } = data || [];

    if (status !== 'success') throw new Error(message);

    const couponsToSet = dataCoupons.map((coupon) => {
      const {
        expiredTime, id, isValid, gotTime,
      } = coupon;

      return {
        ...coupon,
        isPriority: id === dataPriorityCouponId && isValid,
        ...(expiredTime && {
          expirationTimeString: moment(expiredTime).utcOffset(8).format('YYYY/MM/DD HH:mm'),
        }),
        ...(gotTime && {
          gotTimeString: moment(gotTime).utcOffset(8).format('YYYY/MM/DD HH:mm'),
        }),
      };
    }) || [];

    setPriorityCouponId(dataPriorityCouponId || '');
    setCoupons(couponsToSet);
    setCouponsDict(keyBy(couponsToSet, 'id'));
    setVoucherShopItems(dataVoucherShopItems || []);
    setVoucherShopItemDict(keyBy(dataVoucherShopItems, 'id') || {});
    setIsInited(true);
  }, [setCoupons, token, setPriorityCouponId, setCouponsDict]);

  const getCoupons = useCallback(async () => {
    setIsLoading(true);

    try {
      await loadCoupons();
    } catch (e) {
      // ignore the error
    }

    setIsLoading(false);
  }, [setIsLoading, loadCoupons]);

  const setPriorityCoupon = useCallback(async (couponId) => {
    setIsLoading(true);

    try {
      const setNull = priorityCouponId === couponId;
      const result = await apiSetPriorityCoupon(token, setNull ? null : couponId);
      const { status, message } = result || {};

      if (status !== 'success') throw new Error(message);

      await loadCoupons();
    } catch (e) {
      // ignore the error
    }

    setIsLoading(false);
  }, [token, setIsLoading, priorityCouponId, loadCoupons]);

  return {
    coupons,
    couponsDict,
    customerCouponFrontItems,
    voucherShopItems,
    voucherShopItemDict,
    getCoupons,
    isInited,
    isLoading,
    setPriorityCoupon,
  };
};

const [CouponStoreProvider, useCouponStoreContext] = createUseContext(useCouponsStore);

export {
  CouponStoreProvider,
  useCouponStoreContext,
};
